/**
 * Places d'une session de stage.
 *
 * Le calcul « capacité moins inscrits » était recopié dans plusieurs écrans.
 * Il vit ici, une seule fois, et ne descend jamais sous zéro : une session
 * surréservée est complète, pas « à -2 places ».
 */

export interface SessionPlaces {
  capacity: number;
  current_registrations?: number | null;
}

export function remainingPlaces(session: SessionPlaces): number {
  return Math.max(0, session.capacity - (session.current_registrations || 0));
}

export function isFull(session: SessionPlaces): boolean {
  return remainingPlaces(session) <= 0;
}

/**
 * Ordre de mise en avant : d'abord les sessions avec des places, puis les
 * complètes. Dans chaque groupe, la plus proche date de début d'abord ; à date
 * égale, celle qui a le plus de places. Ne modifie pas le tableau reçu.
 */
export function trierParDisponibilite<T extends SessionPlaces & { start_date: string }>(
  sessions: readonly T[],
): T[] {
  return [...sessions].sort((a, b) => {
    const completA = isFull(a) ? 1 : 0;
    const completB = isFull(b) ? 1 : 0;
    if (completA !== completB) return completA - completB;
    const dateA = new Date(a.start_date).getTime();
    const dateB = new Date(b.start_date).getTime();
    if (dateA !== dateB) return dateA - dateB;
    return remainingPlaces(b) - remainingPlaces(a);
  });
}

export interface SelectionAccueil<T> {
  /** Les cartes à afficher, déjà triées et plafonnées. */
  cartes: T[];
  /** Des sessions correspondent, mais aucune n'a de place. */
  toutComplet: boolean;
  /** Plus de sessions correspondent que le plafond n'en montre. */
  voirTout: boolean;
}

/**
 * Ce que la page d'accueil montre à partir des sessions déjà filtrées
 * (centre, âge, semaine, sessions passées exclues).
 */
export function selectionAccueil<T extends SessionPlaces & { start_date: string }>(
  sessions: readonly T[],
  limite: number,
): SelectionAccueil<T> {
  const triees = trierParDisponibilite(sessions);
  return {
    cartes: triees.slice(0, limite),
    toutComplet: triees.length > 0 && triees.every(isFull),
    voirTout: triees.length > limite,
  };
}

/** Au-delà de ce nombre de places restantes, une session n'est plus « presque complète ». */
export const SEUIL_PRESQUE_COMPLET = 3;

export type NiveauPlaces = 'disponible' | 'presque-complet' | 'complet';

export interface LibellePlaces {
  niveau: NiveauPlaces;
  restantes: number;
  capacite: number;
  /** Texte principal : « 5 places disponibles », « Plus qu'1 place »… */
  texte: string;
  /** Version courte pour un badge sur une image. */
  court: string;
  /** Phrase complète pour les lecteurs d'écran. */
  accessible: string;
  /** Part de la capacité déjà occupée, de 0 à 1, pour la jauge. */
  remplissage: number;
}

const places = (n: number) => `${n} place${n > 1 ? 's' : ''}`;

/**
 * Le seul libellé de disponibilité montré aux parents. On dit ce qui reste,
 * en mots : une fraction « 1/8 » se lisait aussi bien « 1 inscrit sur 8 ».
 */
export function libellePlaces(session: SessionPlaces): LibellePlaces {
  const restantes = remainingPlaces(session);
  const capacite = Math.max(0, session.capacity);
  const remplissage = capacite > 0 ? Math.min(1, (capacite - restantes) / capacite) : 1;
  const surCapacite = capacite > 0 ? ` sur ${capacite}` : '';

  if (restantes <= 0) {
    return {
      niveau: 'complet',
      restantes,
      capacite,
      texte: "Complet – liste d'attente possible",
      court: 'Complet',
      accessible: "Complet, liste d'attente possible",
      remplissage,
    };
  }
  if (restantes <= SEUIL_PRESQUE_COMPLET) {
    const texte = restantes === 1 ? "Plus qu'1 place" : `Plus que ${places(restantes)}`;
    return {
      niveau: 'presque-complet',
      restantes,
      capacite,
      texte,
      court: texte,
      accessible: `${texte} disponible${restantes > 1 ? 's' : ''}${surCapacite}`,
      remplissage,
    };
  }
  const texte = `${places(restantes)} disponibles`;
  return {
    niveau: 'disponible',
    restantes,
    capacite,
    texte,
    court: texte,
    accessible: `${texte}${surCapacite}`,
    remplissage,
  };
}
